// Simulation screen (WF-18) — dry-run a published framework over sample subjects
// WITHOUT persisting anything: no results stored, no events, no payroll touched.
// Shows grade distribution, salary-band placement distribution, how many subjects
// a live cycle would flag with a compensation impact, and the promotion-ready
// candidates. This is the "see the outcome before you publish/run" surface.
//
// docs/workforce-framework/rule-engine.md §6 (simulation = the interpreter in
// dry-run) + implementation-plan.md WF-18.
import React, { useEffect, useMemo, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { useTranslation } from 'react-i18next';
import { FlaskConical, Loader2, Play, Plus, Trash2, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { useHasPermission } from '@/rbac/useRbac';
import {
  getFramework, isNotRolledOut, listFrameworks, simulateFramework,
  type FrameworkDetail, type Framework, type SimSubject, type SimulationResult,
} from '@/services/workforceService';

const inputCls =
  'w-full h-9 px-3 rounded-md border border-border bg-card text-sm disabled:bg-muted disabled:text-muted-foreground';

const errMsg = (e: any, fallback: string) => getApiErrorMessage(e, fallback);

interface Draft {
  ref: string;
  levels: Record<string, string>; // factor_key -> level_key
  current_pay?: string;
}

const BAND_LABEL: Record<string, string> = {
  below_min: 'Below band',
  in_band: 'In band',
  above_max: 'Above band',
  no_band: 'No band / unknown pay',
};

export const SimulationTab: React.FC = () => {
  const { t } = useTranslation('workforce');
  const canComp = useHasPermission('workforce:comp_impact');

  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<FrameworkDetail | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [result, setResult] = useState<SimulationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [notRolledOut, setNotRolledOut] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const fws = await listFrameworks();
        setFrameworks(fws);
      } catch (e) {
        if (isNotRolledOut(e)) setNotRolledOut(true);
        // Anything else (e.g. the gateway's 503 "try again") must not read as an empty list.
        else toast.error(getApiErrorMessage(e, 'Could not load frameworks'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    (async () => {
      try {
        const d = await getFramework(selectedId);
        setDetail(d);
        setDrafts([{ ref: 'Sample 1', levels: {} }]);
        setResult(null);
      } catch (e) {
        toast.error(getApiErrorMessage(e, errMsg(e, 'Could not load the framework')));
      }
    })();
  }, [selectedId]);

  const factors = detail?.definition.factors ?? [];

  const addDraft = () =>
    setDrafts((d) => [...d, { ref: `Sample ${d.length + 1}`, levels: {} }]);
  const removeDraft = (i: number) => setDrafts((d) => d.filter((_, idx) => idx !== i));
  const setDraft = (i: number, patch: Partial<Draft>) =>
    setDrafts((d) => d.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));

  const run = async () => {
    if (!detail) return;
    const subjects: SimSubject[] = drafts.map((d) => ({
      ref: d.ref,
      inputs: {
        factors: factors.map((f) => ({
          factor_key: f.key,
          selected_level: d.levels[f.key] || undefined,
        })),
      },
      current_pay: d.current_pay ? Number(d.current_pay) : undefined,
    }));
    setRunning(true);
    try {
      const r = await simulateFramework(detail.framework.id, subjects);
      setResult(r);
      if (r.validation) toast.error('Framework has validation errors — fix them before simulating.');
    } catch (e) {
      toast.error(getApiErrorMessage(e, errMsg(e, 'Simulation failed')));
    } finally {
      setRunning(false);
    }
  };

  const publishedFrameworks = useMemo(
    () => frameworks.filter((f) => f.status === 'active' || f.status === 'published'),
    [frameworks],
  );

  if (notRolledOut) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          {t('sim_not_ready', {
            defaultValue: 'The workforce engine is not available in this workspace yet.',
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <FlaskConical className="w-4 h-4" />
        {t('sim_intro', {
          defaultValue:
            'Dry-run a published framework over sample people. Nothing is saved — no results, no events, no pay changes.',
        })}
      </div>

      <Card>
        <CardContent className="py-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs text-muted-foreground">
                {t('sim_framework', { defaultValue: 'Published framework' })}
              </label>
              <select
                className={inputCls}
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                disabled={loading}
              >
                <option value="">
                  {t('sim_pick', { defaultValue: 'Pick a framework…' })}
                </option>
                {publishedFrameworks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            {detail && (
              <Button onClick={run} disabled={running || drafts.length === 0}>
                {running ? (
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-1.5" />
                )}
                {t('sim_run', { defaultValue: 'Run simulation' })}
              </Button>
            )}
          </div>

          {detail && (
            <div className="space-y-3">
              {drafts.map((d, i) => (
                <div key={i} className="rounded-md border border-border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      className={inputCls + ' max-w-[180px]'}
                      value={d.ref}
                      onChange={(e) => setDraft(i, { ref: e.target.value })}
                    />
                    {canComp && (
                      <input
                        className={inputCls + ' max-w-[160px]'}
                        type="number"
                        placeholder={t('sim_pay', { defaultValue: 'Current pay (optional)' })}
                        value={d.current_pay ?? ''}
                        onChange={(e) => setDraft(i, { current_pay: e.target.value })}
                      />
                    )}
                    {drafts.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeDraft(i)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {factors.map((f) => (
                      <div key={f.key}>
                        <label className="text-xs text-muted-foreground">{f.label}</label>
                        <select
                          className={inputCls}
                          value={d.levels[f.key] || ''}
                          onChange={(e) =>
                            setDraft(i, { levels: { ...d.levels, [f.key]: e.target.value } })
                          }
                        >
                          <option value="">—</option>
                          {f.levels.map((lv) => (
                            <option key={lv.key} value={lv.key}>
                              {lv.label}
                              {typeof lv.score === 'number' ? ` (${lv.score})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addDraft}>
                <Plus className="w-4 h-4 mr-1.5" />
                {t('sim_add', { defaultValue: 'Add sample subject' })}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {result && !result.validation && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="text-sm font-medium mb-2">
                {t('sim_grades', { defaultValue: 'Grade distribution' })}
              </div>
              <ul className="space-y-1 text-sm">
                {Object.entries(result.grade_distribution).map(([g, n]) => (
                  <li key={g} className="flex justify-between">
                    <span>{g || t('sim_ungraded', { defaultValue: 'Ungraded' })}</span>
                    <Badge variant="secondary">{n}</Badge>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="text-sm font-medium mb-2">
                {t('sim_bands', { defaultValue: 'Band placement' })}
              </div>
              {Object.keys(result.band_distribution).length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('sim_no_bands', {
                    defaultValue: 'No salary bands defined (or no pay provided).',
                  })}
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {Object.entries(result.band_distribution).map(([pos, n]) => (
                    <li key={pos} className="flex justify-between">
                      <span>{BAND_LABEL[pos] ?? pos}</span>
                      <Badge variant={pos === 'in_band' ? 'secondary' : 'destructive'}>{n}</Badge>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 pt-3 border-t border-border flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('sim_comp_impact', { defaultValue: 'Comp impact' })}
                </span>
                <Badge variant={result.comp_impact_count > 0 ? 'destructive' : 'secondary'}>
                  {result.comp_impact_count}
                </Badge>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="py-4">
              <div className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-4 h-4" />
                {t('sim_promotion', { defaultValue: 'Promotion ready' })}
              </div>
              {result.promotion_ready.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('sim_no_promotion', { defaultValue: 'No promotion-ready candidates.' })}
                </p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  {result.promotion_ready.map((p) => (
                    <li key={p.ref}>
                      <div className="font-medium">{p.ref}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.grade} · {p.reason}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SimulationTab;
